import {Component, OnInit, ViewChild} from '@angular/core';

import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {TranslateModule} from '@ngx-translate/core';
import {ReactiveFormsModule, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {RolesService} from '../../../core/services/roles/roles.service';
import {RoleModel} from '../../../core/models/roles/role.model';
import {UserGroupService} from '../../../core/services/user/user-group.service';
import {
  CreateUserGroupRequest,
  UpdateUserGroupRequest,
  UserGroupDetailModel,
  UserGroupMemberModel
} from '../../../core/models/user/user-group-detail.model';
import {PickList, PickListModule} from 'primeng/picklist';
import {
  createPaginationState,
  setPaginationLoading,
  updatePaginationFromResult,
} from '../../../core/helpers/pagination-state.helper';
import {PaginationComponent} from '../../../shared/components/pagination/pagination.component';
import {FeatherPipe} from '../../../core/pipes/feather.pipe';

@Component({
  selector: 'app-user-group-manage',
  imports: [
    AlertComponent,
    LoadingComponent,
    TranslateModule,
    ReactiveFormsModule,
    ButtonComponent,
    PickListModule,
    PaginationComponent,
    FeatherPipe,
    RouterLink
],
  templateUrl: './user-group-manage.component.html',
  styleUrl: './user-group-manage.component.scss'
})
export class UserGroupManageComponent implements OnInit {
  isCreateMode = false;
  groupId: number | null = null;
  loading = true;
  submitting = false;
  error: string | null = null;

  group: UserGroupDetailModel | null = null;
  deselectedColumns: RoleModel[] = [];
  selectedColumns: RoleModel[] = [];

  members: UserGroupMemberModel[] = [];
  readonly paginationModel = createPaginationState();

  form!: FormGroup;
  @ViewChild('pickList', {static: false}) pickList!: PickList;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly userGroupService: UserGroupService,
    private readonly rolesService: RolesService
  ) {
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('groupId');
    this.isCreateMode = !idParam;
    this.groupId = idParam ? Number(idParam) : null;

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
      description: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(256)]],
      roles: this.fb.array<string>([])
    });

    this.loadRoles();
    this.loadUsers(this.paginationModel().currentPage, this.paginationModel().itemsPerPage);

    if (!this.isCreateMode && this.groupId) {
      this.userGroupService.getById(this.groupId).subscribe({
        next: (group) => {
          this.group = group;
          this.form.patchValue({
            name: group.name,
            description: group.description
          });
          this.selectedColumns = this.group?.roles.map(r => ({name: r.name, normalizedName: r.normalizedName})) ?? [];
          if (this.deselectedColumns.length > 0) { // all roles loaded first
            this.deselectedColumns = this.deselectedColumns.filter(r => !this.selectedColumns.find(x => x.normalizedName === r.normalizedName));
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = err.message || 'Falha ao carregar grupo.';
          this.loading = false;
        }
      });
    } else {
      this.loading = false;
    }
  }

  private loadRoles(): void {
    this.rolesService.getRoles().subscribe({
      next: roles => {
        this.deselectedColumns = roles.filter(r => !this.selectedColumns.find(x => x.normalizedName === r.normalizedName));
      },
      error: err => {
        console.error(err);
        this.deselectedColumns = [];
      }
    })
  }

  loadUsers = (page: number, pageSize: number): void => {
    if (this.groupId) {
      setPaginationLoading(this.paginationModel, true);
      this.members = [];
      this.userGroupService.getGroupUsers(this.groupId, page, pageSize).subscribe({
        next: (result) => {
          this.members = result.content;
          updatePaginationFromResult(this.paginationModel, result, result.page.size ?? pageSize);
        },
        error: (err) => {
          console.error(err);
          setPaginationLoading(this.paginationModel, false);
        }
      });
    }
  }

  get rolesArray(): string[] {
    return this.selectedColumns.map(x => x.normalizedName);
  }

  submit(): void {
    this.error = null;
    this.submitting = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.submitting = false;
      return;
    }

    const formValue = this.form.value;
    console.log(formValue);
    console.log(this.selectedColumns);
    console.log(this.rolesArray);

    if (this.isCreateMode) {
      const payload: CreateUserGroupRequest = {
        name: formValue.name,
        description: formValue.description,
        roles: this.rolesArray
      };
      this.userGroupService.create(payload).subscribe({
        next: (res) => this.router.navigate(['/users/groups/edit', res.id]),
        error: (err) => {
          this.error = err.message || 'Falha ao criar grupo de usuário.';
          this.submitting = false;
        }
      })
    } else if (this.groupId) {
      const payload: UpdateUserGroupRequest = {
        name: formValue.name,
        description: formValue.description,
        roles: this.rolesArray,
        users: [] // Placeholder until API for users is available
      };
      this.userGroupService.update(this.groupId, payload).subscribe({
        next: () => {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/users/groups/edit', this.groupId]);
          });
        },
        error: (err) => {
          this.error = err.message || 'Falha ao atualizar grupo de usuário.';
          this.submitting = false;
        }
      })
    }
  }

  cancel(): void {
    this.router.navigate(['/users/groups/list']);
  }

  onMoveAllToTarget(event: any): void {
    // Use event payload per PrimeNG guidance to avoid relying on internal component state
    this.selectedColumns = [...this.selectedColumns, ...(event?.items ?? [])];
    this.deselectedColumns = this.deselectedColumns.filter(r => !this.selectedColumns.find(x => x.normalizedName === r.normalizedName));
  }

  // onMoveToTarget(event: any): void {
  //   // this.selectedColumns = [...this.selectedColumns, ...(event?.items ?? [])];
  //   // this.deselectedColumns = this.deselectedColumns.filter(r => !this.selectedColumns.find(x => x.normalizedName === r.normalizedName));
  // }

  // onMoveToSource(event: any): void {
  //   // this.deselectedColumns = [...this.deselectedColumns, ...(event?.items ?? [])];
  //   // this.selectedColumns = this.selectedColumns.filter(r => !this.deselectedColumns.find(x => x.normalizedName === r.normalizedName));
  // }

  onMoveAllToSource(event: any): void {
    this.deselectedColumns = [...this.deselectedColumns, ...(event?.items ?? [])];
    this.selectedColumns = this.selectedColumns.filter(r => !this.deselectedColumns.find(x => x.normalizedName === r.normalizedName));
  }
}
